import { useEffect, useState } from 'react';
import { Alert, Button, Collapse, Form, Spinner } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';
import { mascaraCPF, mascaraTelefoneComZero } from 'utils/helpers';
import { BUSCA_PACIENTE_AJUDA, hasAdvancedFilters } from 'utils/pacientesSearch';

const inputBuscaStyle = {
  border: '1px solid var(--neutral-200)',
  borderRadius: 10,
  background: 'var(--background-color)',
};

const labelStyle = {
  display: 'block',
  marginBottom: 12,
};

function CampoFiltro({ id, label, className = '', children }) {
  return (
    <Form.Group className={className} controlId={id}>
      <Form.Label className="fw-semibold" style={labelStyle} htmlFor={id}>
        {label}
      </Form.Label>
      {children}
    </Form.Group>
  );
}

export default function BuscaPacienteForm({
  filtros,
  onChange,
  onSubmit,
  onLimpar,
  isLoading = false,
  buscaErro = '',
  invalidField = '',
  embedded = false,
}) {
  const [mobileAdvancedOpen, setMobileAdvancedOpen] = useState(() => hasAdvancedFilters(filtros));

  useEffect(() => {
    if (hasAdvancedFilters(filtros)) {
      setMobileAdvancedOpen(true);
    }
  }, [filtros.codPac, filtros.cpf, filtros.celular, filtros.dtNasc]);

  const setField = (field, value) => {
    onChange({ ...filtros, [field]: value });
  };

  const nomeInvalid = invalidField === 'nome';
  const codPacInvalid = invalidField === 'codPac';
  const cpfInvalid = invalidField === 'cpf';
  const celularInvalid = invalidField === 'celular';

  const botoesAcao = (className = '') => (
    <div className={`d-flex flex-wrap gap-2 ${className}`}>
      <Button
        type="submit"
        variant="primary"
        className="rounded-pill px-4"
        disabled={isLoading}
        aria-busy={isLoading}
        aria-label="Localizar pacientes"
      >
        {isLoading ? (
          <Spinner animation="border" size="sm" aria-hidden="true" />
        ) : (
          <>
            <FaSearch size={10} className="me-2" aria-hidden="true" />
            Localizar
          </>
        )}
      </Button>
      <Button
        type="button"
        variant="outline-secondary"
        className="rounded-pill px-4"
        onClick={onLimpar}
        disabled={isLoading}
      >
        Limpar
      </Button>
    </div>
  );

  const campoCodigo = (id, className) => (
    <CampoFiltro id={id} label="Código" className={className}>
      <Form.Control
        id={id}
        style={inputBuscaStyle}
        value={filtros.codPac}
        isInvalid={codPacInvalid}
        onChange={(e) => setField('codPac', e.target.value)}
        inputMode="numeric"
      />
      {codPacInvalid && <Form.Control.Feedback type="invalid">{buscaErro}</Form.Control.Feedback>}
    </CampoFiltro>
  );

  const campoCpf = (id, className) => (
    <CampoFiltro id={id} label="CPF" className={className}>
      <Form.Control
        id={id}
        style={inputBuscaStyle}
        placeholder="Somente números"
        inputMode="numeric"
        maxLength={14}
        value={filtros.cpf}
        isInvalid={cpfInvalid}
        onChange={(e) => setField('cpf', mascaraCPF(e.target.value))}
      />
      {cpfInvalid && <Form.Control.Feedback type="invalid">{buscaErro}</Form.Control.Feedback>}
    </CampoFiltro>
  );

  const campoCelular = (id, className) => (
    <CampoFiltro id={id} label="Celular" className={className}>
      <Form.Control
        id={id}
        style={inputBuscaStyle}
        placeholder="Ex: (014)90000-1111"
        inputMode="tel"
        maxLength={14}
        value={filtros.celular}
        isInvalid={celularInvalid}
        onChange={(e) => setField('celular', mascaraTelefoneComZero(e.target.value))}
      />
      {celularInvalid && <Form.Control.Feedback type="invalid">{buscaErro}</Form.Control.Feedback>}
    </CampoFiltro>
  );

  const campoDtNasc = (id, className) => (
    <CampoFiltro id={id} label="Data de Nascimento" className={className}>
      <Form.Control
        id={id}
        style={inputBuscaStyle}
        type="date"
        value={filtros.dtNasc}
        onChange={(e) => setField('dtNasc', e.target.value)}
      />
    </CampoFiltro>
  );

  const campoNome = (id, className) => (
    <CampoFiltro id={id} label="Nome do Paciente" className={className}>
      <Form.Control
        id={id}
        style={{ ...inputBuscaStyle, textTransform: 'uppercase' }}
        value={filtros.nome}
        isInvalid={nomeInvalid}
        autoFocus={!embedded}
        onChange={(e) => setField('nome', e.target.value)}
      />
      {nomeInvalid && <Form.Control.Feedback type="invalid">{buscaErro}</Form.Control.Feedback>}
    </CampoFiltro>
  );

  return (
    <>
      <p className="text-muted small mb-3">{BUSCA_PACIENTE_AJUDA}</p>

      <Form className="row g-2 align-items-end" onSubmit={onSubmit}>
        {campoNome('busca-paciente-nome', 'col-12 d-lg-none')}
        <div className="col-12 d-lg-none mb-2">{botoesAcao('justify-content-center')}</div>

        <div className="col-12 d-lg-none">
          <Button
            type="button"
            variant="link"
            className="p-0 text-decoration-none small"
            onClick={() => setMobileAdvancedOpen((open) => !open)}
            aria-expanded={mobileAdvancedOpen}
            aria-controls="busca-paciente-mais-filtros"
          >
            {mobileAdvancedOpen ? (
              <>
                <FaChevronUp className="me-1" aria-hidden="true" />
                Menos filtros
              </>
            ) : (
              <>
                <FaChevronDown className="me-1" aria-hidden="true" />
                Mais filtros
              </>
            )}
          </Button>
          <Collapse in={mobileAdvancedOpen}>
            <div id="busca-paciente-mais-filtros" className="row g-2 mt-2">
              {campoCodigo('busca-paciente-codPac', 'col-12')}
              {campoCpf('busca-paciente-cpf', 'col-12')}
              {campoCelular('busca-paciente-celular', 'col-12')}
              {campoDtNasc('busca-paciente-dtNasc', 'col-12')}
            </div>
          </Collapse>
        </div>

        {campoNome('busca-paciente-nome-desktop', 'col-12 col-lg-6 d-none d-lg-block')}
        {campoCodigo('busca-paciente-codPac-desktop', 'col-12 col-lg-3 d-none d-lg-block')}

        <div className="col-12 col-lg-3 d-none d-lg-flex align-items-end justify-content-lg-end pb-1">
          {botoesAcao()}
        </div>

        {campoCpf('busca-paciente-cpf-desktop', 'col-12 col-lg-3 d-none d-lg-block')}
        {campoCelular('busca-paciente-celular-desktop', 'col-12 col-lg-3 d-none d-lg-block')}
        {campoDtNasc('busca-paciente-dtNasc-desktop', 'col-12 col-lg-3 d-none d-lg-block')}
      </Form>

      {buscaErro && !invalidField && (
        <Alert variant="warning" className="mt-3 mb-0 py-2 small" role="alert">
          {buscaErro}
        </Alert>
      )}
    </>
  );
}
